import "./aiforme.scss";

import { Breadcrumbs, CrumbProps } from "@amzn/stencil-react-components/breadcrumbs";
import { Col, Container, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { AiForMeCustomBuiltDetails } from "common/components/AIForMe/AiForMeCustomBuiltDetails";
import { AiForMeIntakeForm } from "common/components/AIForMe/AiForMeIntakeForm";
import { AiForMeReviewAndSubmit } from "common/components/AIForMe/AiForMeReviewAndSubmit";
import { AiForMeThirdPartyDetails } from "common/components/AIForMe/AiForMeThirdPartyDetails";
import { AiForMeFormData, PlatformName } from "common/components/AIForMe/constants/platform-constants";
import { castToolToFormData } from "common/components/AIForMe/helpers/cast-tool-to-form-data";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { AIForMeTool } from "lib/models/aiforme-models";
import { UserPermissions } from "lib/services";
import React, { FC, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

const pageSectionPadding = {
  paddingTop: 32,
  paddingBottom: 32,
};

const FormSteps = {
  INTAKE: 1,
  CUSTOM_BUILT: 2,
  THIRD_PARTY: 3,
  REVIEW: 4,
};

const AiForMeIntakePage: FC = () => {
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState<number>(FormSteps.INTAKE);
  const [formData, setFormData] = useState<AiForMeFormData | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    if (location.state) {
      const locationState = (location.state as AIForMeTool).item.data;
      setFormData(castToolToFormData(locationState));
      setIsEdit(true);
    }
    document.title = getString("aiForMe.title");
  }, []);

  const { addErrorBanner } = useMessageBannersBox();

  const handleNext = (data: Partial<AiForMeFormData>) => {
    const updatedFormData = { ...formData, ...data } as AiForMeFormData;
    setFormData(updatedFormData);

    if (currentStep === FormSteps.INTAKE) {
      if (updatedFormData.platformName === PlatformName.MODEL_HOSTING) {
        setCurrentStep(FormSteps.CUSTOM_BUILT);
      } else if (updatedFormData.platformName === PlatformName.THIRD_PARTY) {
        setCurrentStep(FormSteps.THIRD_PARTY);
      }
    } else {
      setCurrentStep(FormSteps.REVIEW);
    }
  };

  const handleBack = (data?: Partial<AiForMeFormData>) => {
    const updatedFormData = { ...formData, ...data } as AiForMeFormData;
    setFormData(updatedFormData);

    if (currentStep === FormSteps.REVIEW) {
      if (updatedFormData.platformName === PlatformName.MODEL_HOSTING) {
        setCurrentStep(FormSteps.CUSTOM_BUILT);
      } else if (updatedFormData.platformName === PlatformName.THIRD_PARTY) {
        setCurrentStep(FormSteps.THIRD_PARTY);
      }
    } else {
      setCurrentStep(FormSteps.INTAKE);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case FormSteps.INTAKE:
        return <AiForMeIntakeForm onNext={handleNext} initialData={formData} isEdit={isEdit} />;
      case FormSteps.CUSTOM_BUILT:
        return <AiForMeCustomBuiltDetails formData={formData!} onNext={handleNext} onBack={handleBack} />;
      case FormSteps.THIRD_PARTY:
        return <AiForMeThirdPartyDetails formData={formData!} onNext={handleNext} onBack={handleBack} />;
      case FormSteps.REVIEW:
        return <AiForMeReviewAndSubmit formData={formData!} onBack={handleBack} isEdit={isEdit} />;
      default:
        return <AiForMeIntakeForm onNext={handleNext} initialData={formData} isEdit={isEdit} />;
    }
  };

  // Check permissions on component mount
  useEffect(() => {
    if (!UserPermissions.current.hasAuthorized) {
      // User hasn't been authorized yet, wait for authorization
      return;
    }

    if (!UserPermissions.current.hasPermission(PERMISSIONS.AI_FOR_ME)) {
      // Only show error if we have valid permissions but user doesn't have this specific permission
      // If permissions are expired, the UserPermissions service will handle the session expired redirect
      const hasValidPermissions = UserPermissions.current.permissions.length > 0;
      if (hasValidPermissions) {
        addErrorBanner(getString("errors.notAuthorized"), false);
        history.push("/");
      }
    }
  }, []);

  return (
    <>
      <div className="aiforme-gradient-back">
        <PageMainContainer>
          <Row>
            <Breadcrumbs
              crumbs={[
                {
                  title: getString("logoText"),
                  url: "/",
                },
                {
                  title: getString("aiForMe.title"),
                  url: "/aiforme",
                },
                {
                  title: getString("aiForMe.contribute"),
                },
              ]}
              onCrumbClick={
                /* istanbul ignore next */
                (e, crumb: CrumbProps) => {
                  if (crumb.url) {
                    e.preventDefault();
                    history.push(crumb.url);
                  }
                }
              }
            />
          </Row>
          <Row justifyContent="space-between" alignItems="flex-end">
            <Col flex={2}>
              <Text fontSize={"T500"} fontWeight="bold" style={{ paddingBottom: 20 }}>
                {isEdit ? getString("aiForMe.editTool") : getString("aiForMe.contributeTool")}
              </Text>
              {isEdit && (
                <Text style={{ paddingBottom: 20 }}>
                  {getString("aiForMe.slaInfo1")}
                  <br />
                  {getString("aiForMe.slaInfo2")}
                </Text>
              )}
            </Col>
            <Col flex={1}>
              <img
                src="/img/aiforme-people.svg"
                alt="Two people working using AI tools"
                style={{
                  maxWidth: 468,
                  width: "100%",
                  height: "auto",
                }}
                data-testid="people-img"
              />
            </Col>
          </Row>
        </PageMainContainer>
      </div>
      <div className="aiforme-content">
        <PageMainContainer>
          <Container {...pageSectionPadding}>{renderCurrentStep()}</Container>
        </PageMainContainer>
      </div>
    </>
  );
};

export default AiForMeIntakePage;
